/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type {
  BidReviewResult,
  CvMatchResult,
  PipelineFailureReason,
  ProcurementOverview,
  SourceRef,
  SourceSnippet,
  TenderDocument,
} from "../../types"
import type { GPTModelItem, GraphFileItem, UserDocument } from "@/api/models"
import type { TenderExtractionApi } from "@/api/tender-api"

import { downloadGraphFileApi } from "@/api/api"
import { downloadBidReviewDocx, getTenderDocumentUrl } from "@/api/tender-api"
import {
  AlertTriangle,
  CheckCircle2,
  FileDown,
  FileSearch,
  FileText,
  Info,
  Lightbulb,
  Loader2,
  Plus,
  RotateCcw,
  ShieldAlert,
  StopCircle,
  Upload,
  X,
  XCircle,
} from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { CitationSidebarHeader } from "@/features/chat/components/citation-sidebar-header"
import { CitationViewerContent } from "@/features/chat/components/citation-viewer-content"
import { FilePickerDialog } from "@/features/chat/components/file-picker-dialog"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"

import { filterAllowedFiles, TENDER_ACCEPT_FILTER } from "../../tender-upload-constants"
import { LlmTraceModal } from "../llm-trace-modal"
import { TenderAiDisclaimer } from "../tender-ai-disclaimer"
import { ComplianceTab } from "./bid-review-compliance-tab"
import { BidReviewFrameworkSelector } from "./bid-review-framework-selector"
import { FrameworkTab } from "./bid-review-framework-tab"
import { RiskTab } from "./bid-review-risk-tab"
import { ScoringTab } from "./bid-review-scoring-tab"
import { StaffingTab } from "./bid-review-staffing-tab"
import { SummaryTab } from "./bid-review-summary-tab"
import { type OpenSourceFn } from "./extraction-citation"
import { buildDocumentNameLookup, buildExtractionLookup } from "./extraction-citation-utils"

type CvMatchQueryData = {
  result: CvMatchResult | null
  status?: string | null
}

// ── Upload state (no docs yet) ────────────────────────────────────────────────

type UploadStateProps = {
  caseId: string
  runId: string
  cvMatchQuery?: CvMatchQueryData
  isUploading: boolean
  isActionDisabled?: boolean
  title?: string
  description?: string
  infoMessage?: string
  secondaryActionLabel?: string
  onSecondaryAction?: () => void
  onUpload: (files: File[]) => void
}

function UploadState({
  cvMatchQuery,
  isUploading,
  isActionDisabled = false,
  title,
  description,
  infoMessage,
  secondaryActionLabel,
  onSecondaryAction,
  onUpload,
}: UploadStateProps) {
  const { t } = useTranslation()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const isUploadDisabled = isUploading || isActionDisabled
  const displayTitle = title ?? t("tender.bidReview.section.uploadTitle")
  const displayDescription = description ?? t("tender.bidReview.section.uploadDescription")

  const handleGraphFileSelect = async (item: GraphFileItem) => {
    const token = SettingsService.getInstance().token
    const blob = await downloadGraphFileApi(item, token)
    const mime = item.mimeType || blob.type || "application/octet-stream"
    onUpload([new File([blob], item.name, { type: mime })])
  }

  const handleDocumentsSelected = (documents: UserDocument[]) => {
    const files = documents.map((doc) => {
      const extension = doc.contentFormat === "markdown" ? ".md" : ".txt"
      const safeName = (doc.title || "untitled-document").replace(/[/\\?%*:|"<>]/g, "-")
      return new File([doc.content], `${safeName}${extension}`, { type: "text/plain" })
    })
    onUpload(files)
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">{displayTitle}</h2>
        <p className="text-muted-foreground mt-1 text-sm leading-relaxed">{displayDescription}</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {(
          [
            {
              icon: ShieldAlert,
              titleKey: "tender.bidReview.section.featureComplianceTitle",
              descKey: "tender.bidReview.section.featureComplianceDesc",
            },
            {
              icon: CheckCircle2,
              titleKey: "tender.bidReview.section.featureContentTitle",
              descKey: "tender.bidReview.section.featureContentDesc",
            },
            {
              icon: Lightbulb,
              titleKey: "tender.bidReview.section.featureImprovementTitle",
              descKey: "tender.bidReview.section.featureImprovementDesc",
            },
          ] as const
        ).map(({ icon: Icon, titleKey, descKey }) => (
          <div key={titleKey} className="bg-muted/30 rounded-lg border p-3">
            <div className="mb-1.5 flex items-center gap-2">
              <Icon className="text-muted-foreground size-3.5" />
              <span className="text-[12px] font-medium">{t(titleKey)}</span>
            </div>
            <p className="text-muted-foreground text-[11px] leading-relaxed">{t(descKey)}</p>
          </div>
        ))}
      </div>

      <CvContextPanel cvMatchQuery={cvMatchQuery} />

      <div
        aria-disabled={isUploadDisabled || undefined}
        role="button"
        tabIndex={isUploadDisabled ? -1 : 0}
        className={cn(
          "border-border/60 bg-muted/20 flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-8 py-10 transition-colors",
          isUploadDisabled
            ? "cursor-not-allowed opacity-60"
            : "hover:border-border hover:bg-muted/30 cursor-pointer",
          isDragOver && !isUploadDisabled && "border-primary bg-primary/5"
        )}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => {
          if (isUploadDisabled) return
          setPickerOpen(true)
        }}
        onDragEnter={(e) => {
          e.preventDefault()
          if (!isUploadDisabled) setIsDragOver(true)
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false)
        }}
        onDrop={(e) => {
          e.preventDefault()
          setIsDragOver(false)
          if (isUploadDisabled) return
          const { allowed } = filterAllowedFiles(Array.from(e.dataTransfer.files))
          if (allowed.length > 0) onUpload(allowed)
        }}
        onKeyDown={(e) => {
          if (e.key !== "Enter" || isUploadDisabled) return
          setPickerOpen(true)
        }}
      >
        {isUploading ? (
          <>
            <Loader2 className="text-muted-foreground size-8 animate-spin" />
            <p className="text-muted-foreground text-sm">
              {t("tender.bidReview.section.uploading")}
            </p>
          </>
        ) : (
          <>
            <div className="bg-muted flex size-10 items-center justify-center rounded-full">
              <Upload className="text-muted-foreground size-5" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium">{t("tender.bidReview.section.dropPrompt")}</p>
              <p className="text-muted-foreground mt-0.5 text-xs">
                {t("tender.bidReview.section.fileTypesHint")}
              </p>
            </div>
          </>
        )}
      </div>

      <FilePickerDialog
        accept={TENDER_ACCEPT_FILTER}
        open={pickerOpen}
        source="recent"
        onDocumentsSelected={handleDocumentsSelected}
        onLocalFilesSelected={onUpload}
        onOpenChange={setPickerOpen}
        onSelect={handleGraphFileSelect}
      />

      {(secondaryActionLabel ?? infoMessage) && (
        <div className="flex items-center gap-2">
          {secondaryActionLabel && onSecondaryAction && (
            <Button
              disabled={isUploadDisabled}
              size="sm"
              variant="outline"
              onClick={onSecondaryAction}
            >
              {secondaryActionLabel}
            </Button>
          )}
          {infoMessage && (
            <p className="text-muted-foreground text-xs leading-relaxed">{infoMessage}</p>
          )}
        </div>
      )}
    </div>
  )
}

// ── Ready state (docs uploaded, review not started) ───────────────────────────

const KNOWN_FRAMEWORKS = ["ABK 09", "AB 04", "ABT 06", "AMA AF 12", "LOU", "GDPR"]

type StartBidReviewHandler = (frameworks: string[], modelId?: string) => Promise<unknown> | void

function getPipelineFailureMessage(
  t: (key: string) => string,
  failureReason?: PipelineFailureReason,
  error?: string
) {
  if (failureReason === "user_cancelled") return t("tender.abort.failureReasons.user_cancelled")
  if (failureReason === "worker_lost") return t("tender.abort.failureReasons.worker_lost")
  if (failureReason === "hard_timeout") return t("tender.abort.failureReasons.hard_timeout")
  if (failureReason === "rate_limited_exhausted")
    return t("tender.abort.failureReasons.rate_limited_exhausted")
  return error ?? t("tender.abort.failureReasons.pipeline_error")
}

type ReadyStateProps = {
  caseId: string
  runId: string
  cvMatchQuery?: CvMatchQueryData
  uploadedDocs: TenderDocument[]
  isUploading: boolean
  isRemoving: boolean
  isStarting: boolean
  extractionCompleted: boolean
  bidDocsReady: boolean
  autoDetectedFrameworks: string[]
  selectedFrameworks: string[]
  showModelSelector?: boolean
  availableModels?: GPTModelItem[]
  headerTitle?: string
  headerDescription?: string
  infoMessage?: string
  startButtonLabel?: string
  startButtonLoadingLabel?: string
  secondaryActionLabel?: string
  onSecondaryAction?: () => void
  isActionDisabled?: boolean
  onUpload: (files: File[]) => void
  onRemove: (docId: string) => void
  onStart: StartBidReviewHandler
  onFrameworksChange: (frameworks: string[]) => void
}

function ReadyState({
  cvMatchQuery,
  uploadedDocs,
  isUploading,
  isRemoving,
  isStarting,
  extractionCompleted,
  bidDocsReady,
  autoDetectedFrameworks,
  selectedFrameworks,
  showModelSelector = false,
  availableModels = [],
  headerTitle,
  headerDescription,
  infoMessage,
  startButtonLabel,
  startButtonLoadingLabel,
  secondaryActionLabel,
  onSecondaryAction,
  isActionDisabled = false,
  onUpload,
  onRemove,
  onStart,
  onFrameworksChange,
}: ReadyStateProps) {
  const { t } = useTranslation()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [selectedModelId, setSelectedModelId] = useState<string | undefined>(undefined)
  const areDocumentActionsDisabled = isActionDisabled || isUploading || isRemoving || isStarting
  const resolvedStartLabel = startButtonLabel ?? t("tender.bidReview.section.startReview")
  const resolvedStartLoadingLabel =
    startButtonLoadingLabel ?? t("tender.bidReview.section.startingReview")

  const handleGraphFileSelect = async (item: GraphFileItem) => {
    const token = SettingsService.getInstance().token
    const blob = await downloadGraphFileApi(item, token)
    const mime = item.mimeType || blob.type || "application/octet-stream"
    onUpload([new File([blob], item.name, { type: mime })])
  }

  const handleDocumentsSelected = (documents: UserDocument[]) => {
    const files = documents.map((doc) => {
      const extension = doc.contentFormat === "markdown" ? ".md" : ".txt"
      const safeName = (doc.title || "untitled-document").replace(/[/\\?%*:|"<>]/g, "-")
      return new File([doc.content], `${safeName}${extension}`, { type: "text/plain" })
    })
    onUpload(files)
  }

  return (
    <div className="flex flex-col items-center gap-5 py-8">
      <div className="bg-success/15 flex size-12 items-center justify-center rounded-full">
        <FileText className="text-success size-5" />
      </div>

      {(headerTitle ?? headerDescription) && (
        <div className="max-w-md text-center">
          {headerTitle && <h2 className="text-lg font-semibold tracking-tight">{headerTitle}</h2>}
          {headerDescription && (
            <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
              {headerDescription}
            </p>
          )}
        </div>
      )}

      <div className="flex w-full max-w-sm flex-col gap-1.5">
        {uploadedDocs.map((doc) => (
          <div
            key={doc.id}
            className="bg-muted/20 flex items-center gap-2 rounded-lg border px-3 py-2"
          >
            <FileText className="text-muted-foreground size-3.5 shrink-0" />
            <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{doc.filename}</span>
            <Button
              className="text-muted-foreground/40 hover:text-destructive size-7 shrink-0 p-0 disabled:opacity-30"
              disabled={areDocumentActionsDisabled}
              size="icon"
              type="button"
              variant="ghost"
              onClick={() => onRemove(doc.id)}
            >
              <X className="size-3.5" />
            </Button>
          </div>
        ))}
      </div>

      <Button
        disabled={areDocumentActionsDisabled}
        type="button"
        variant="ghost"
        className={cn(
          "text-muted-foreground/45 hover:text-muted-foreground/75 hover:bg-muted/40 h-auto gap-1.5 rounded-md px-3 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-50",
          isDragOver &&
            !areDocumentActionsDisabled &&
            "bg-primary/10 text-primary/70 ring-primary/30 ring-1"
        )}
        onClick={() => setPickerOpen(true)}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={(e) => {
          e.preventDefault()
          if (!areDocumentActionsDisabled) setIsDragOver(true)
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false)
        }}
        onDrop={(e) => {
          e.preventDefault()
          setIsDragOver(false)
          if (areDocumentActionsDisabled) return
          const { allowed } = filterAllowedFiles(Array.from(e.dataTransfer.files))
          if (allowed.length > 0) onUpload(allowed)
        }}
      >
        {isUploading ? <Loader2 className="size-3 animate-spin" /> : <Plus className="size-3" />}
        {t("tender.bidReview.section.addMoreDocuments")}
      </Button>

      <FilePickerDialog
        accept={TENDER_ACCEPT_FILTER}
        open={pickerOpen}
        source="recent"
        onDocumentsSelected={handleDocumentsSelected}
        onLocalFilesSelected={onUpload}
        onOpenChange={setPickerOpen}
        onSelect={handleGraphFileSelect}
      />

      <div className="w-full max-w-sm">
        <BidReviewFrameworkSelector
          autoDetectedFrameworks={autoDetectedFrameworks}
          selectedFrameworks={selectedFrameworks}
          onSelectionChange={onFrameworksChange}
        />
      </div>

      <CvContextPanel cvMatchQuery={cvMatchQuery} />

      {infoMessage && (
        <p className="text-muted-foreground max-w-sm text-center text-xs leading-relaxed">
          {infoMessage}
        </p>
      )}
      {!bidDocsReady && (
        <p className="text-muted-foreground max-w-xs text-center text-xs">
          {t("tender.bidReview.section.waitingDocumentsProcessing")}
        </p>
      )}
      {bidDocsReady && !extractionCompleted && (
        <p className="text-muted-foreground max-w-xs text-center text-xs">
          {t("tender.bidReview.section.extractionRequiredBeforeReview")}
        </p>
      )}
      <div className="flex items-center gap-2">
        {showModelSelector && availableModels.length > 0 && (
          <Select
            value={selectedModelId ?? ""}
            onValueChange={(v) => setSelectedModelId(v || undefined)}
          >
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue placeholder={t("tender.intake.defaultModelPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {availableModels.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {secondaryActionLabel && onSecondaryAction && (
          <Button
            disabled={areDocumentActionsDisabled}
            size="sm"
            variant="outline"
            onClick={onSecondaryAction}
          >
            {secondaryActionLabel}
          </Button>
        )}
        <Button
          disabled={areDocumentActionsDisabled || !extractionCompleted || !bidDocsReady}
          size="sm"
          onClick={() => void onStart(selectedFrameworks, selectedModelId)}
        >
          {isStarting ? (
            <>
              <Loader2 className="mr-2 size-3.5 animate-spin" />
              {resolvedStartLoadingLabel}
            </>
          ) : (
            <>
              <CheckCircle2 className="mr-2 size-3.5" />
              {resolvedStartLabel}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

// ── CV context preview (pre-run) ──────────────────────────────────────────────

type CvContextPanelProps = {
  cvMatchQuery?: CvMatchQueryData
}

function CvContextPanel({ cvMatchQuery }: CvContextPanelProps) {
  const { t } = useTranslation()
  const cvMatchResult = cvMatchQuery?.result
  const cvMatchStatus = cvMatchQuery?.status
  const lockedCandidates = cvMatchResult?.lockedCandidates ?? []
  const hasLockedCandidates = lockedCandidates.length > 0

  if (hasLockedCandidates) {
    const roleNameById = new Map(
      (cvMatchResult?.rolePools ?? []).map((p) => [p.roleId, p.roleName])
    )

    return (
      <div className="border-success bg-success/10 rounded-lg border px-3.5 py-2.5">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-success flex items-center gap-1.5 text-[11px] font-semibold tracking-wide uppercase">
            <CheckCircle2 className="size-3 shrink-0" />
            <span>{t("tender.bidReview.staffingPlanHeading")}</span>
          </div>
          <span className="text-success text-[11px] tabular-nums">
            {t("tender.bidReview.lockedCandidates", { count: lockedCandidates.length })}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          {lockedCandidates.map((lc) => (
            <div key={lc.candidateId} className="flex items-baseline gap-1.5 text-[11px]">
              <span className="text-success font-medium">{lc.displayName}</span>
              <span className="text-success min-w-0 truncate">
                · {roleNameById.get(lc.roleId) ?? lc.roleId}
              </span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!cvMatchStatus || cvMatchStatus === "pending") {
    return (
      <div className="border-muted-foreground/20 text-muted-foreground/60 flex items-center gap-2 rounded-lg border border-dashed px-3.5 py-2.5 text-xs">
        <Info className="size-3.5 shrink-0" />
        <span>{t("tender.bidReview.staffingBanner.incomplete")}</span>
      </div>
    )
  }

  return (
    <div className="border-muted-foreground/20 text-muted-foreground/60 flex items-center gap-2 rounded-lg border border-dashed px-3.5 py-2.5 text-xs">
      <Info className="size-3.5 shrink-0" />
      <span>{t("tender.bidReview.staffingBanner.noPlanUnlocked")}</span>
    </div>
  )
}

// ── Tabbed results state ──────────────────────────────────────────────────────

const TAB_CONFIG = [
  { id: "sammanfattning", labelKey: "tender.bidReview.tabs.summary", pass: "synthesis" },
  { id: "kravuppfyllelse", labelKey: "tender.bidReview.tabs.compliance", pass: "compliance" },
  { id: "poangbedomning", labelKey: "tender.bidReview.tabs.scoring", pass: "scoring" },
  { id: "ramverk", labelKey: "tender.bidReview.tabs.framework", pass: "framework" },
  { id: "risker", labelKey: "tender.bidReview.tabs.risk", pass: "risk" },
  { id: "bemanning", labelKey: "tender.bidReview.tabs.staffing", pass: "staffing" },
] as const

// ── Staffing plan integration banner ──────────────────────────────────────────

type StaffingPlanBannerProps = {
  cvMatchQuery?: CvMatchQueryData
  staffingPlanIncluded?: boolean
  reviewCompletedAt?: string
  onStaleChange?: (isStale: boolean) => void
}

function StaffingPlanBanner({
  cvMatchQuery,
  staffingPlanIncluded,
  reviewCompletedAt,
  onStaleChange,
}: StaffingPlanBannerProps) {
  const { t } = useTranslation()
  const cvMatchResult = cvMatchQuery?.result
  const cvMatchStatus = cvMatchQuery?.status
  const hasLockedCandidates = (cvMatchResult?.lockedCandidates.length ?? 0) > 0

  const isStale = useMemo(() => {
    if (!staffingPlanIncluded || !reviewCompletedAt || !hasLockedCandidates) return false
    const reviewDate = new Date(reviewCompletedAt).getTime()
    const latestLock = Math.max(
      ...cvMatchResult!.lockedCandidates.map((lc) => new Date(lc.lockedAt).getTime())
    )
    return latestLock > reviewDate
  }, [staffingPlanIncluded, reviewCompletedAt, hasLockedCandidates, cvMatchResult])

  useEffect(() => {
    onStaleChange?.(isStale)
  }, [isStale, onStaleChange])

  if (isStale) {
    return (
      <div className="border-warning bg-warning/10 text-warning-foreground flex items-center gap-2 rounded-lg border px-3 py-2 text-xs">
        <AlertTriangle className="size-3.5 shrink-0" />
        <span>{t("tender.bidReview.staffingBanner.stale")}</span>
      </div>
    )
  }

  if (staffingPlanIncluded) {
    return (
      <div className="text-success border-success bg-success/15 flex items-center gap-2 rounded-lg border px-3 py-2 text-xs">
        <CheckCircle2 className="size-3.5 shrink-0" />
        <span>{t("tender.bidReview.staffingBanner.included")}</span>
      </div>
    )
  }

  if (cvMatchStatus && cvMatchStatus !== "pending" && !hasLockedCandidates) {
    return (
      <div className="border-warning bg-warning/10 text-warning-foreground flex items-center gap-2 rounded-lg border px-3 py-2 text-xs">
        <Info className="size-3.5 shrink-0" />
        <span>{t("tender.bidReview.staffingBanner.noneIncludedRerun")}</span>
      </div>
    )
  }

  if (!cvMatchStatus || cvMatchStatus === "pending") {
    return (
      <div className="border-warning bg-warning/10 text-warning-foreground flex items-center gap-2 rounded-lg border px-3 py-2 text-xs">
        <Info className="size-3.5 shrink-0" />
        <span>{t("tender.bidReview.staffingBanner.noneIncludedPending")}</span>
      </div>
    )
  }

  return null
}

// ── Tabbed results ────────────────────────────────────────────────────────────

type TabbedResultsStateProps = {
  caseId: string
  runId: string
  caseTitle: string
  review: BidReviewResult
  bidDocuments: TenderDocument[]
  extractionData?: TenderExtractionApi
  cvMatchQuery?: CvMatchQueryData
  isProcessing: boolean
  staleReviewMessage?: string
  canEdit?: boolean
  isAbortPending?: boolean
  onStaffingPlanStaleChange?: (isStale: boolean) => void
  onAbort?: () => void
  onOpenTrace: () => void
  onRerun: () => void
  onRetryPasses: (passes: string[]) => void
}

function TabbedResultsState({
  caseId,
  runId,
  caseTitle,
  review,
  bidDocuments,
  extractionData,
  cvMatchQuery,
  isProcessing,
  staleReviewMessage,
  canEdit = true,
  isAbortPending = false,
  onStaffingPlanStaleChange,
  onAbort,
  onOpenTrace,
  onRerun,
  onRetryPasses,
}: TabbedResultsStateProps) {
  const { t, i18n } = useTranslation()
  const extractionLookup = useMemo(() => buildExtractionLookup(extractionData), [extractionData])
  const docNameLookup = useMemo(
    () => buildDocumentNameLookup(bidDocuments, extractionData),
    [bidDocuments, extractionData]
  )

  const openDocumentPreview = useCallback<OpenSourceFn>(
    (source: SourceRef | SourceSnippet) => {
      if (source.kind !== "file") return
      const docId = source.documentId
      if (!docId) return
      const documentUrl = getTenderDocumentUrl({
        caseId,
        docId,
        filename: source.documentName,
        page: source.page,
      })
      const title = source.documentName
      useContentSidebarStore.getState().openPanel({
        id: "tender-document-preview",
        title,
        headerMode: "custom",
        renderHeader: (context) => (
          <CitationSidebarHeader
            closeable={context.closeable}
            downloadUrl={documentUrl}
            isFocusable={context.isFocusable}
            isFocused={context.isFocused}
            title={title}
            onClose={context.onClose}
            onToggleFocus={context.onToggleFocus}
          />
        ),
        content: (
          <CitationViewerContent
            chunkSnippet={"chunkText" in source ? (source.chunkText ?? "") : ""}
            documentUrl={documentUrl}
            quoteSnippet={"quote" in source ? source.quote : ""}
          />
        ),
        focusable: true,
        closeable: true,
        resizable: true,
        isDirty: () => false,
      })
    },
    [caseId]
  )

  const completedPasses = review.completedPasses ?? []
  const passErrors = review.passErrors ?? {}
  const completedCount = completedPasses.length
  const hasPendingPasses =
    isProcessing &&
    TAB_CONFIG.some(({ pass }) => !completedPasses.includes(pass) && !passErrors[pass])

  let defaultTab = "sammanfattning"
  if (!completedPasses.includes("synthesis") && completedPasses.includes("compliance")) {
    defaultTab = "kravuppfyllelse"
  }

  const [activeTab, setActiveTab] = useState(defaultTab)
  let abortControl: React.ReactNode = null

  if (canEdit && isProcessing && onAbort) {
    abortControl = isAbortPending ? (
      <Button disabled size="sm" variant="outline">
        <Loader2 className="mr-2 size-4 animate-spin" />
        {t("tender.abort.pending")}
      </Button>
    ) : (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            className="hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive"
            size="sm"
            variant="outline"
          >
            <StopCircle className="mr-2 size-4" />
            {t("tender.abort.action")}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("tender.abort.bidReview.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("tender.abort.bidReview.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("tender.abort.keepRunning")}</AlertDialogCancel>
            <AlertDialogAction onClick={onAbort}>{t("tender.abort.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">
            {t("tender.bidReview.results.heading")}
          </h2>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {review.bidDocumentFilenames.map((name) => (
              <span
                key={name}
                className="bg-muted/40 text-muted-foreground flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs"
              >
                <FileText className="size-3 shrink-0" />
                <span className="max-w-[200px] truncate">{name}</span>
              </span>
            ))}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <Button
            size="sm"
            title={t("tender.extraction.llmTraceTitleAttr")}
            variant="outline"
            onClick={onOpenTrace}
          >
            <FileSearch className="mr-2 size-4" />
            {t("tender.extraction.llmTraceButton")}
          </Button>
          {abortControl}
          {!isProcessing && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => void downloadBidReviewDocx(caseId, runId, caseTitle)}
            >
              <FileDown className="mr-2 size-4" />
              {t("tender.extraction.exportDocx")}
            </Button>
          )}
          {canEdit && !isProcessing && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  title={t("tender.bidReview.rerun.buttonTooltip")}
                  variant="outline"
                >
                  <RotateCcw className="mr-2 size-4" />
                  {t("tender.bidReview.retryPass")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("tender.bidReview.rerun.title")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("tender.bidReview.rerun.description")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="sm:justify-between">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void downloadBidReviewDocx(caseId, runId, caseTitle)}
                  >
                    <FileDown className="mr-2 size-3.5" />
                    {t("tender.bidReview.rerun.downloadDocx")}
                  </Button>
                  <div className="flex gap-2">
                    <AlertDialogCancel>{t("tender.quickStart.cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={onRerun}>
                      {t("tender.bidReview.rerun.newReview")}
                    </AlertDialogAction>
                  </div>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <TenderAiDisclaimer />

      {isProcessing &&
      review.rateLimitRetry?.nextRetryAt &&
      !Number.isNaN(Date.parse(review.rateLimitRetry.nextRetryAt)) ? (
        <div className="border-warning/30 bg-warning/10 text-warning-foreground rounded-md border px-3 py-2 text-xs">
          {t("tender.rateLimit.waiting", {
            time: new Date(review.rateLimitRetry.nextRetryAt).toLocaleString(i18n.language, {
              dateStyle: "short",
              timeStyle: "short",
            }),
          })}
        </div>
      ) : null}

      {staleReviewMessage && (
        <div className="border-warning bg-warning/10 text-warning-foreground flex items-start gap-2 rounded-lg border px-3 py-2 text-xs">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>{staleReviewMessage}</span>
        </div>
      )}

      {(hasPendingPasses || Object.keys(passErrors).length > 0) && (
        <div className="text-muted-foreground flex items-center gap-2 text-[12px]">
          {hasPendingPasses && <Loader2 className="size-3 animate-spin" />}
          <span>
            {t("tender.bidReview.results.analysesProgress", { completed: completedCount })}
          </span>
          <div className="flex items-center gap-1">
            {TAB_CONFIG.map(({ pass }) => {
              const hasFailed = !!passErrors[pass]
              const isComplete = completedPasses.includes(pass)
              let dotColor = "bg-muted-foreground/30"
              if (hasFailed) dotColor = "bg-destructive-ink"
              else if (isComplete) dotColor = "bg-success-ink"
              return <div key={pass} className={cn("size-2 rounded-full", dotColor)} title={pass} />
            })}
          </div>
        </div>
      )}

      {!isProcessing && (
        <StaffingPlanBanner
          cvMatchQuery={cvMatchQuery}
          reviewCompletedAt={review.completedAt}
          staffingPlanIncluded={review.staffingPlanIncluded}
          onStaleChange={onStaffingPlanStaleChange}
        />
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          {TAB_CONFIG.map(({ id, labelKey, pass }) => {
            const isComplete = completedPasses.includes(pass)
            const hasFailed = !!passErrors[pass]
            const isPending = isProcessing && !isComplete && !hasFailed
            const staffingRisk =
              id === "bemanning" && isComplete
                ? review.staffingAnalysis?.staffingRiskLevel
                : undefined

            return (
              <TabsTrigger key={id} className="text-[13px]" value={id}>
                {t(labelKey)}
                {isPending && <Loader2 className="ml-1 size-3 animate-spin" />}
                {hasFailed && !isPending && (
                  <AlertTriangle className="text-destructive ml-1 size-3" />
                )}
                {isComplete && !isPending && !hasFailed && staffingRisk === "critical" && (
                  <span className="bg-destructive-ink ml-1 inline-block size-1.5 rounded-full" />
                )}
                {isComplete && !isPending && !hasFailed && staffingRisk === "high" && (
                  <span className="bg-primary-ink ml-1 inline-block size-1.5 rounded-full" />
                )}
                {isComplete && !isPending && !hasFailed && staffingRisk === "medium" && (
                  <span className="bg-warning-ink ml-1 inline-block size-1.5 rounded-full" />
                )}
                {isComplete && !isPending && !hasFailed && staffingRisk === "low" && (
                  <span className="bg-success-ink ml-1 inline-block size-1.5 rounded-full" />
                )}
                {isComplete && !isPending && !hasFailed && !staffingRisk && (
                  <span className="bg-success-ink ml-1 inline-block size-1.5 rounded-full" />
                )}
              </TabsTrigger>
            )
          })}
        </TabsList>

        <TabsContent value="sammanfattning">
          {passErrors["synthesis"] && (
            <div className="border-destructive bg-destructive/[0.04] mb-4 rounded-lg border p-4">
              <div className="flex items-start gap-3">
                <XCircle className="text-destructive mt-0.5 size-4 shrink-0" />
                <div className="flex-1">
                  <p className="text-destructive text-sm font-medium">
                    {t("tender.bidReview.passFailed.synthesis")}
                  </p>
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {t("tender.abort.failureReasons.pipeline_error")}
                  </p>
                </div>
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={() => onRetryPasses(["synthesis"])}>
                    <RotateCcw className="mr-1.5 size-3.5" />
                    {t("tender.bidReview.retryPass")}
                  </Button>
                )}
              </div>
            </div>
          )}
          <SummaryTab
            competitiveNotes={review.competitiveNotes}
            extractionLookup={extractionLookup}
            goNoGo={review.goNoGo}
            goNoGoReasoning={review.goNoGoReasoning}
            improvementOpportunities={review.improvementOpportunities}
            isLoading={!completedPasses.includes("synthesis") && !passErrors["synthesis"]}
            overallAssessment={review.overallAssessment}
            priorityItems={review.priorityItems}
            strengthsSummary={review.strengthsSummary}
            totalMaxScore={review.totalMaxScore}
            totalPredictedScore={review.totalPredictedScore}
            onOpenSource={openDocumentPreview}
          />
        </TabsContent>

        <TabsContent value="kravuppfyllelse">
          {passErrors["compliance"] && (
            <div className="border-destructive bg-destructive/[0.04] mb-4 rounded-lg border p-4">
              <div className="flex items-start gap-3">
                <XCircle className="text-destructive mt-0.5 size-4 shrink-0" />
                <div className="flex-1">
                  <p className="text-destructive text-sm font-medium">
                    {t("tender.bidReview.passFailed.compliance")}
                  </p>
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {t("tender.abort.failureReasons.pipeline_error")}
                  </p>
                </div>
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={() => onRetryPasses(["compliance"])}>
                    <RotateCcw className="mr-1.5 size-3.5" />
                    {t("tender.bidReview.retryPass")}
                  </Button>
                )}
              </div>
            </div>
          )}
          <ComplianceTab
            deliverableAnalyses={review.deliverableAnalyses}
            docNameLookup={docNameLookup}
            extractionLookup={extractionLookup}
            isLoading={!completedPasses.includes("compliance") && !passErrors["compliance"]}
            requirementAnalyses={review.requirementAnalyses}
            roleAnalyses={review.roleAnalyses}
            onOpenSource={openDocumentPreview}
          />
        </TabsContent>

        <TabsContent value="poangbedomning">
          {passErrors["scoring"] && (
            <div className="border-destructive bg-destructive/[0.04] mb-4 rounded-lg border p-4">
              <div className="flex items-start gap-3">
                <XCircle className="text-destructive mt-0.5 size-4 shrink-0" />
                <div className="flex-1">
                  <p className="text-destructive text-sm font-medium">
                    {t("tender.bidReview.passFailed.scoring")}
                  </p>
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {t("tender.abort.failureReasons.pipeline_error")}
                  </p>
                </div>
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={() => onRetryPasses(["scoring"])}>
                    <RotateCcw className="mr-1.5 size-3.5" />
                    {t("tender.bidReview.retryPass")}
                  </Button>
                )}
              </div>
            </div>
          )}
          <ScoringTab
            criterionScores={review.criterionScores}
            extractionLookup={extractionLookup}
            isLoading={!completedPasses.includes("scoring") && !passErrors["scoring"]}
            scoringNotes={review.scoringNotes}
            totalMaxScore={review.totalMaxScore}
            totalPredictedScore={review.totalPredictedScore}
            onOpenSource={openDocumentPreview}
          />
        </TabsContent>

        <TabsContent value="ramverk">
          {passErrors["framework"] && (
            <div className="border-destructive bg-destructive/[0.04] mb-4 rounded-lg border p-4">
              <div className="flex items-start gap-3">
                <XCircle className="text-destructive mt-0.5 size-4 shrink-0" />
                <div className="flex-1">
                  <p className="text-destructive text-sm font-medium">
                    {t("tender.bidReview.passFailed.framework")}
                  </p>
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {t("tender.abort.failureReasons.pipeline_error")}
                  </p>
                </div>
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={() => onRetryPasses(["framework"])}>
                    <RotateCcw className="mr-1.5 size-3.5" />
                    {t("tender.bidReview.retryPass")}
                  </Button>
                )}
              </div>
            </div>
          )}
          <FrameworkTab
            extractionLookup={extractionLookup}
            frameworkAnalyses={review.frameworkAnalyses}
            isLoading={!completedPasses.includes("framework") && !passErrors["framework"]}
            onOpenSource={openDocumentPreview}
          />
        </TabsContent>

        <TabsContent value="risker">
          {passErrors["risk"] && (
            <div className="border-destructive bg-destructive/[0.04] mb-4 rounded-lg border p-4">
              <div className="flex items-start gap-3">
                <XCircle className="text-destructive mt-0.5 size-4 shrink-0" />
                <div className="flex-1">
                  <p className="text-destructive text-sm font-medium">
                    {t("tender.bidReview.passFailed.risk")}
                  </p>
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {t("tender.abort.failureReasons.pipeline_error")}
                  </p>
                </div>
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={() => onRetryPasses(["risk"])}>
                    <RotateCcw className="mr-1.5 size-3.5" />
                    {t("tender.bidReview.retryPass")}
                  </Button>
                )}
              </div>
            </div>
          )}
          <RiskTab
            contractAnalyses={review.contractAnalyses}
            extractionLookup={extractionLookup}
            isLoading={!completedPasses.includes("risk") && !passErrors["risk"]}
            timelineChecks={review.timelineChecks}
            onOpenSource={openDocumentPreview}
          />
        </TabsContent>

        <TabsContent value="bemanning">
          {passErrors["staffing"] && (
            <div className="border-destructive bg-destructive/[0.04] mb-4 rounded-lg border p-4">
              <div className="flex items-start gap-3">
                <XCircle className="text-destructive mt-0.5 size-4 shrink-0" />
                <div className="flex-1">
                  <p className="text-destructive text-sm font-medium">
                    {t("tender.bidReview.passFailed.staffing")}
                  </p>
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {t("tender.abort.failureReasons.pipeline_error")}
                  </p>
                </div>
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={() => onRetryPasses(["staffing"])}>
                    <RotateCcw className="mr-1.5 size-3.5" />
                    {t("tender.bidReview.retryPass")}
                  </Button>
                )}
              </div>
            </div>
          )}
          <StaffingTab
            staffingAnalysis={review.staffingAnalysis}
            staffingPlanIncluded={review.staffingPlanIncluded}
            isLoading={
              review.staffingPlanIncluded === true &&
              !completedPasses.includes("staffing") &&
              !passErrors["staffing"]
            }
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ── Loading skeleton placeholder for initial processing ───────────────────────

function ProcessingTabsSkeleton({
  filenames,
  abortRequested,
  canEdit,
  onAbort,
}: {
  filenames: string[]
  abortRequested: boolean
  canEdit?: boolean
  onAbort?: () => void
}) {
  const { t } = useTranslation()
  let abortControl: React.ReactNode

  if (canEdit && onAbort) {
    abortControl = abortRequested ? (
      <Button disabled size="sm" variant="outline">
        <Loader2 className="mr-2 size-3.5 animate-spin" />
        {t("tender.abort.pending")}
      </Button>
    ) : (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            className="hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive"
            size="sm"
            variant="outline"
          >
            <StopCircle className="mr-2 size-4" />
            {t("tender.abort.action")}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("tender.abort.bidReview.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("tender.abort.bidReview.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("tender.abort.keepRunning")}</AlertDialogCancel>
            <AlertDialogAction onClick={onAbort}>{t("tender.abort.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )
  } else {
    abortControl = <Skeleton className="h-8 w-28" />
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">
            {t("tender.bidReview.processing.heading")}
          </h2>
          <p className="text-muted-foreground mt-0.5 text-sm">{filenames.join(", ")}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3">{abortControl}</div>
      </div>
      <div className="text-muted-foreground flex items-center gap-2 text-[12px]">
        <Loader2 className="size-3 animate-spin" />
        <span>0/5 analyser klara</span>
        <div className="flex items-center gap-1">
          {TAB_CONFIG.map(({ pass }) => (
            <div key={pass} className="bg-muted-foreground/30 size-2 rounded-full" />
          ))}
        </div>
      </div>
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

type BidReviewSectionProps = {
  caseId: string
  runId: string
  caseTitle: string
  bidDocuments: TenderDocument[]
  bidReview?: BidReviewResult
  cvMatchQuery?: CvMatchQueryData
  extractionData?: TenderExtractionApi
  isBidReviewRunning: boolean
  isUploadingBidDocument: boolean
  isRemovingBidDocument: boolean
  extractionCompleted: boolean
  bidDocsReady: boolean
  procurementOverview?: ProcurementOverview
  showModelSelector?: boolean
  availableModels?: GPTModelItem[]
  canEdit?: boolean
  isAborting?: boolean
  onUploadBidDocument: (files: File[]) => void
  onRemoveBidDocument: (docId: string) => void
  onAbortBidReview?: () => void
  onStartBidReview: StartBidReviewHandler
  onRetryBidReviewPasses: (passes: string[]) => void
  onReviewStaleChange?: (isStale: boolean) => void
}

export function BidReviewSection({
  caseId,
  runId,
  caseTitle,
  bidDocuments,
  bidReview,
  cvMatchQuery,
  extractionData,
  isBidReviewRunning,
  isUploadingBidDocument,
  isRemovingBidDocument,
  extractionCompleted,
  bidDocsReady,
  procurementOverview,
  showModelSelector = false,
  availableModels = [],
  canEdit = true,
  isAborting = false,
  onUploadBidDocument,
  onRemoveBidDocument,
  onAbortBidReview,
  onStartBidReview,
  onRetryBidReviewPasses,
  onReviewStaleChange,
}: BidReviewSectionProps) {
  const { t } = useTranslation()
  const [traceModalOpen, setTraceModalOpen] = useState(false)
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>([])
  const [hasManualFrameworkSelection, setHasManualFrameworkSelection] = useState(false)
  const [isPreparingRerun, setIsPreparingRerun] = useState(false)
  const [isTransitioningToRerun, setIsTransitioningToRerun] = useState(false)
  const [isStaffingPlanStale, setIsStaffingPlanStale] = useState(false)
  const [lastCompletedReviewSnapshot, setLastCompletedReviewSnapshot] = useState<{
    documentIdsKey: string
    frameworksKey: string
  } | null>(null)
  const [rerunPreparationSnapshot, setRerunPreparationSnapshot] = useState<{
    documentIdsKey: string
    frameworksKey: string
  } | null>(null)

  const autoDetectedFrameworks = useMemo(() => {
    const overviewText = [procurementOverview?.contractStandard, procurementOverview?.frameworkRef]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()

    return KNOWN_FRAMEWORKS.filter((fw) => overviewText.includes(fw.toLowerCase()))
  }, [procurementOverview?.contractStandard, procurementOverview?.frameworkRef])

  const hasProgressiveResults = bidReview != null && (bidReview.completedPasses?.length ?? 0) > 0
  const isProcessing = isBidReviewRunning || bidReview?.status === "processing"
  const isAbortPending = isAborting || bidReview?.cancelRequested === true
  const failureMessage = getPipelineFailureMessage(t, bidReview?.failureReason, bidReview?.error)
  const currentDocumentIdsKey = useMemo(() => {
    return bidDocuments
      .map((doc) => doc.id)
      .sort()
      .join("|")
  }, [bidDocuments])
  const currentDocumentFilenamesKey = useMemo(() => {
    return bidDocuments
      .map((doc) => doc.filename)
      .sort()
      .join("|")
  }, [bidDocuments])
  const currentFrameworksKey = useMemo(() => {
    return [...selectedFrameworks].sort().join("|")
  }, [selectedFrameworks])
  const reviewDocumentFilenamesKey = useMemo(() => {
    return [...(bidReview?.bidDocumentFilenames ?? [])].sort().join("|")
  }, [bidReview?.bidDocumentFilenames])
  const reviewFrameworksKey = useMemo(() => {
    return [...(bidReview?.selectedFrameworks ?? [])].sort().join("|")
  }, [bidReview?.selectedFrameworks])
  const hasRerunPreparationChanges =
    isPreparingRerun &&
    rerunPreparationSnapshot != null &&
    (rerunPreparationSnapshot.documentIdsKey !== currentDocumentIdsKey ||
      rerunPreparationSnapshot.frameworksKey !== currentFrameworksKey)
  const isCompletedReviewStale =
    bidReview?.status === "completed" &&
    ((lastCompletedReviewSnapshot != null &&
      (currentDocumentIdsKey !== lastCompletedReviewSnapshot.documentIdsKey ||
        currentFrameworksKey !== lastCompletedReviewSnapshot.frameworksKey)) ||
      currentDocumentFilenamesKey !== reviewDocumentFilenamesKey ||
      currentFrameworksKey !== reviewFrameworksKey)

  useEffect(() => {
    setHasManualFrameworkSelection(false)
    setSelectedFrameworks([])
    setIsPreparingRerun(false)
    setIsTransitioningToRerun(false)
    setIsStaffingPlanStale(false)
    setLastCompletedReviewSnapshot(null)
    setRerunPreparationSnapshot(null)
  }, [runId])

  useEffect(() => {
    if (bidReview?.selectedFrameworks == null) return
    setSelectedFrameworks(bidReview.selectedFrameworks)
  }, [bidReview?.selectedFrameworks, runId])

  useEffect(() => {
    if (isPreparingRerun) return
    if (bidReview != null) return
    if (hasManualFrameworkSelection) return
    setSelectedFrameworks(autoDetectedFrameworks)
  }, [autoDetectedFrameworks, bidReview, hasManualFrameworkSelection, isPreparingRerun])

  const handleFrameworksChange = useCallback((frameworks: string[]) => {
    setHasManualFrameworkSelection(true)
    setSelectedFrameworks(frameworks)
  }, [])

  const handleEnterRerunPreparation = useCallback(() => {
    setRerunPreparationSnapshot({
      documentIdsKey: currentDocumentIdsKey,
      frameworksKey: currentFrameworksKey,
    })
    setIsPreparingRerun(true)
  }, [currentDocumentIdsKey, currentFrameworksKey])

  const handleExitRerunPreparation = useCallback(() => {
    setIsPreparingRerun(false)
    setRerunPreparationSnapshot(null)
  }, [])

  const handleStartBidReview = useCallback<StartBidReviewHandler>(
    async (frameworks, modelId) => {
      const isPreparedRerunStart = isPreparingRerun
      if (isPreparedRerunStart) {
        setIsTransitioningToRerun(true)
      }

      try {
        await onStartBidReview(frameworks, modelId)
      } catch (error) {
        if (isPreparedRerunStart) {
          setIsTransitioningToRerun(false)
        }
        throw error
      }
    },
    [isPreparingRerun, onStartBidReview]
  )

  useEffect(() => {
    if (bidReview?.status !== "processing") return
    setIsPreparingRerun(false)
    setIsTransitioningToRerun(false)
    setIsStaffingPlanStale(false)
    setLastCompletedReviewSnapshot(null)
    setRerunPreparationSnapshot(null)
  }, [bidReview?.status])

  useEffect(() => {
    if (bidReview?.status !== "completed") return
    setLastCompletedReviewSnapshot((currentSnapshot) => {
      if (currentSnapshot != null) return currentSnapshot
      return {
        documentIdsKey: currentDocumentIdsKey,
        frameworksKey: currentFrameworksKey,
      }
    })
  }, [bidReview?.status, currentDocumentIdsKey, currentFrameworksKey])

  useEffect(() => {
    onReviewStaleChange?.(Boolean(isCompletedReviewStale || isStaffingPlanStale))
  }, [isCompletedReviewStale, isStaffingPlanStale, onReviewStaleChange])

  let readyStateHeaderTitle: string | undefined
  let readyStateHeaderDescription: string | undefined
  let readyStateInfoMessage: string | undefined
  let readyStateStartButtonLabel: string | undefined
  let readyStateStartButtonLoadingLabel: string | undefined
  let readyStateSecondaryActionLabel: string | undefined
  let readyStateSecondaryAction: (() => void) | undefined

  if (isPreparingRerun) {
    readyStateHeaderTitle = t("tender.bidReview.prepareRerun.title")
    readyStateHeaderDescription = t("tender.bidReview.prepareRerun.description")
    readyStateInfoMessage = t("tender.bidReview.prepareRerun.infoKeepsResult")
    readyStateStartButtonLabel = t("tender.bidReview.prepareRerun.startRerun")
    readyStateStartButtonLoadingLabel = t("tender.bidReview.prepareRerun.startingRerun")
    readyStateSecondaryActionLabel = t("tender.bidReview.prepareRerun.backToResults")
    readyStateSecondaryAction = handleExitRerunPreparation

    if (hasRerunPreparationChanges) {
      readyStateInfoMessage = t("tender.bidReview.prepareRerun.infoOutdated")
    }
  }

  if (isTransitioningToRerun) {
    return (
      <ProcessingTabsSkeleton
        abortRequested={false}
        filenames={bidDocuments.map((doc) => doc.filename)}
      />
    )
  }

  if (isProcessing && !hasProgressiveResults) {
    const filenames = bidReview?.bidDocumentFilenames ?? bidDocuments.map((d) => d.filename)
    return (
      <ProcessingTabsSkeleton
        abortRequested={isAbortPending}
        canEdit={canEdit}
        filenames={filenames}
        onAbort={onAbortBidReview}
      />
    )
  }

  if (
    !isPreparingRerun &&
    ((isProcessing && hasProgressiveResults) || bidReview?.status === "completed")
  ) {
    return (
      <>
        <TabbedResultsState
          bidDocuments={bidDocuments}
          canEdit={canEdit}
          caseId={caseId}
          caseTitle={caseTitle}
          cvMatchQuery={cvMatchQuery}
          extractionData={extractionData}
          isAbortPending={isAbortPending}
          isProcessing={isProcessing}
          review={bidReview!}
          runId={runId}
          staleReviewMessage={
            isCompletedReviewStale ? t("tender.bidReview.staleReviewMessage") : undefined
          }
          onAbort={onAbortBidReview}
          onOpenTrace={() => setTraceModalOpen(true)}
          onRerun={handleEnterRerunPreparation}
          onRetryPasses={onRetryBidReviewPasses}
          onStaffingPlanStaleChange={setIsStaffingPlanStale}
        />
        <LlmTraceModal
          caseId={caseId}
          caseTitle={caseTitle}
          open={traceModalOpen}
          runId={runId}
          step="bid-review"
          stepLabel={t("tender.bidReview.llmTraceStepLabel")}
          onOpenChange={setTraceModalOpen}
        />
      </>
    )
  }

  if (bidReview?.status === "failed") {
    return (
      <div className="flex flex-col items-center gap-4 py-12 text-center">
        <XCircle className="text-destructive size-8" />
        <div>
          <p className="text-sm font-medium">{t("tender.bidReview.failedState.title")}</p>
          <p className="text-muted-foreground mt-0.5 text-xs">{failureMessage}</p>
        </div>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => void handleStartBidReview(selectedFrameworks)}
          >
            {t("tender.bidReview.failedState.tryAgain")}
          </Button>
        )}
      </div>
    )
  }

  if (bidDocuments.length > 0) {
    return (
      <ReadyState
        autoDetectedFrameworks={autoDetectedFrameworks}
        availableModels={availableModels}
        bidDocsReady={bidDocsReady}
        caseId={caseId}
        cvMatchQuery={cvMatchQuery}
        extractionCompleted={extractionCompleted}
        headerDescription={readyStateHeaderDescription}
        headerTitle={readyStateHeaderTitle}
        infoMessage={readyStateInfoMessage}
        isActionDisabled={!canEdit}
        isRemoving={isRemovingBidDocument}
        isStarting={isBidReviewRunning || isTransitioningToRerun}
        isUploading={isUploadingBidDocument}
        runId={runId}
        secondaryActionLabel={readyStateSecondaryActionLabel}
        selectedFrameworks={selectedFrameworks}
        showModelSelector={showModelSelector}
        startButtonLabel={readyStateStartButtonLabel}
        startButtonLoadingLabel={readyStateStartButtonLoadingLabel}
        uploadedDocs={bidDocuments}
        onFrameworksChange={handleFrameworksChange}
        onRemove={canEdit ? onRemoveBidDocument : () => undefined}
        onSecondaryAction={readyStateSecondaryAction}
        onStart={canEdit ? handleStartBidReview : () => Promise.resolve()}
        onUpload={canEdit ? onUploadBidDocument : () => undefined}
      />
    )
  }

  return (
    <UploadState
      caseId={caseId}
      cvMatchQuery={cvMatchQuery}
      isUploading={isUploadingBidDocument}
      runId={runId}
      title={isPreparingRerun ? t("tender.bidReview.prepareRerun.title") : undefined}
      description={
        isPreparingRerun ? t("tender.bidReview.prepareRerun.descriptionNeedDoc") : undefined
      }
      infoMessage={
        isPreparingRerun ? t("tender.bidReview.prepareRerun.infoKeepsResult") : undefined
      }
      isActionDisabled={
        !canEdit || isRemovingBidDocument || isBidReviewRunning || isTransitioningToRerun
      }
      secondaryActionLabel={
        isPreparingRerun ? t("tender.bidReview.prepareRerun.backToResults") : undefined
      }
      onSecondaryAction={isPreparingRerun ? handleExitRerunPreparation : undefined}
      onUpload={canEdit ? onUploadBidDocument : () => undefined}
    />
  )
}
